import { ColoredDatePickerIcon } from "@smartsheet/lodestar-icons";
import { getClientId } from "@smartsheet/lodestar-utils";
import { RequiredThemeKeys } from "@smartsheet/sds-theme-internal";
import { CalendarIcon } from "@smartsheet/ursa-icons";
import clsx from "clsx";
import React, { ButtonHTMLAttributes, forwardRef } from "react";
import { useI18n } from "../../../../i18n/i18n";
import { useTheme } from "../../../../theme/utils/useTheme";
import { InputFieldBaseProps } from "../../../../util/inputFieldBaseProps";
import { ButtonAppearances, ButtonSizes } from "../../../button";
import { DatePickerCalendarButton, DatePickerCalendarButtonProps } from "../styled/DatePickerCalendarButton";

export interface DatePickerInputCalendarButtonProps
    extends ButtonHTMLAttributes<HTMLButtonElement>,
        InputFieldBaseProps {
    clientId: string | undefined;
    isDisabled: boolean;
    isReadOnly: boolean;
}

export const DatePickerInputCalendarButton = forwardRef<HTMLButtonElement, DatePickerInputCalendarButtonProps>(
    ({ clientId, isDisabled, isReadOnly, inputFieldHeight, ...rest }, ref) => {
        const { key } = useTheme();
        const themeMode = key as RequiredThemeKeys;
        const { i18n } = useI18n();
        const isUrsaLight = themeMode.includes("ursa");

        const sharedProps = {
            ...rest,
            "aria-label": i18n._(/*i18n*/ "date picker"),
            isDisabled,
            clientId: getClientId(clientId, "dp-calendar-button"),
            ref: ref as React.Ref<DatePickerCalendarButtonProps>,
        };

        const ursaProps = isUrsaLight
            ? {
                  appearance: "secondary-empty" as ButtonAppearances,
                  iconBefore: (
                      <CalendarIcon
                          data-client-id="test1"
                          size={inputFieldHeight === 40 ? 20 : 16}
                          style={inputFieldHeight === 40 ? { marginRight: "2px" } : undefined}
                      />
                  ),
                  size: inputFieldHeight === 40 ? "small" : ("xsmall" as ButtonSizes),
              }
            : {};

        return (
            <DatePickerCalendarButton
                className={clsx((isDisabled || isReadOnly) && `is-disabled`)}
                themeMode={themeMode}
                {...ursaProps}
                {...sharedProps}
            >
                {!isUrsaLight && <ColoredDatePickerIcon />}
            </DatePickerCalendarButton>
        );
    }
);

DatePickerInputCalendarButton.displayName = "DatePickerInputCalendarButton";
